import { useMemo, useState } from 'react';

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxInputGroup,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import type { UserSummary } from '@/lib/api/generated/model';

export interface AssigneePickerProps {
  /** Everyone who may be assigned — the directory, not just people seen before. */
  people: readonly UserSummary[];
  /** The chosen person's id, or `null` for unassigned. */
  value: string | null;
  onChange: (userId: string | null) => void;
  /** Ties a `<label>` to this control. */
  id?: string;
  disabled?: boolean;
  /**
   * What the "nobody chosen" row reads as. Defaults to "Unassigned", which is
   * what choosing it means in the workflow builder — but on a *task*, clearing
   * an override returns the gate to the workflow's own assignee rather than to
   * nobody (spec 067 FR-032), and a row labelled "Unassigned" would name the
   * wrong outcome.
   */
  emptyLabel?: string;
  /**
   * The directory could not be read, so `people` being empty says nothing about
   * who exists. Changes only the no-results text — the control stays usable,
   * because disabling it during a brief outage would take away the ability to
   * clear an assignment too.
   */
  directoryUnavailable?: boolean;
}

/** Case-insensitive match on either half of how a person is known. */
function matches(people: readonly UserSummary[], query: string): UserSummary[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...people];
  return people.filter(
    (person) =>
      person.display_name.toLowerCase().includes(needle) ||
      person.email.toLowerCase().includes(needle),
  );
}

/**
 * Choose the person a Human Gate routes to.
 *
 * Built on the same combobox as `LabelPicker`, which already solved list
 * bounding and pinning the search field. Two differences are deliberate: this
 * one is single-select, because a gate routes to one person (spec 067 FR-025);
 * and it offers an explicit "Unassigned" row rather than only allowing an empty
 * selection, because clearing an assignment is a thing someone *does*, and a
 * control you can only clear by deleting text does not read as offering it.
 *
 * People are shown by name with the address beneath: two colleagues can share a
 * display name and cannot share an address. No avatars — there are none in this
 * product, and a generated one would be decoration standing in for information.
 */
export function AssigneePicker({
  people,
  value,
  onChange,
  id,
  disabled = false,
  emptyLabel = 'Unassigned',
  directoryUnavailable = false,
}: AssigneePickerProps) {
  const [query, setQuery] = useState('');
  const found = useMemo(() => matches(people, query), [people, query]);
  const selected = people.find((person) => person.id === value) ?? null;

  return (
    <div className="flex flex-col gap-1.5">
      <Combobox<string, false>
        value={value ?? ''}
        onValueChange={(next) => {
          onChange(next ? next : null);
        }}
        items={['', ...found.map((person) => person.id)]}
        disabled={disabled}
        // The items are identifiers; what a reader types is a name or an address.
        // Without this, Base UI compares the query against a UUID and matches
        // nothing — the same trap `LabelPicker` documents.
        itemToStringLabel={(personId) =>
          personId
            ? (people.find((person) => person.id === personId)?.display_name ??
              personId)
            : emptyLabel
        }
      >
        <ComboboxInputGroup>
          <ComboboxInput
            id={id}
            placeholder="Search people…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </ComboboxInputGroup>

        <ComboboxContent>
          <ComboboxList>
            <ComboboxItem value="">
              <span className="text-muted-foreground">{emptyLabel}</span>
            </ComboboxItem>
            {found.map((person) => (
              <ComboboxItem key={person.id} value={person.id}>
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="truncate">{person.display_name}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {person.email}
                  </span>
                </span>
              </ComboboxItem>
            ))}
          </ComboboxList>

          {found.length === 0 && (
            // A dead end is worse than a wrong answer: say what happened and what
            // to do, rather than showing an empty box (UX guideline: no-results).
            <ComboboxEmpty>
              <span className="text-muted-foreground text-sm">
                {directoryUnavailable
                  ? 'The list of people could not be loaded. Anyone already assigned stays assigned; try again shortly.'
                  : `No one matches “${query.trim()}”. Try part of a name or address.`}
              </span>
            </ComboboxEmpty>
          )}
        </ComboboxContent>
      </Combobox>

      {/*
        Who it currently routes to, said outside the control.

        The input above is bound to what a reader is *typing*, which is the only
        way the list can be filtered by name or address — so it cannot also
        display the standing choice. Without this line the field reads as empty
        whether a gate routes to somebody or to nobody, which are the two things
        it exists to tell apart.
      */}
      <p className="text-muted-foreground text-xs">
        {selected ? (
          <span className="text-foreground">{selected.display_name}</span>
        ) : (
          emptyLabel
        )}
      </p>
    </div>
  );
}
